import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '@card-software/library';
import { SecondQualityDispatch, SecondQualityDispatchStatus } from '../models/second-quality-dispatch/second-quality-dispatch';
import {
  SecondQualityDispatchSearchCommand,
  SecondQualityDispatchInsertCommand,
  SecondQualityDispatchShippingUpdateCommand,
  SecondQualityDispatchBillingUpdateCommand,
  SecondQualityDispatchItemLinesUpdateCommand,
  SecondQualityDispatchRollPriceUpdateCommand,
  SecondQualityDispatchLotItemPriceUpdateCommand,
} from '../models/second-quality-dispatch/second-quality-dispatch-commands';
import { SecondQualityDispatchSearchResult } from '../models/second-quality-dispatch/second-quality-dispatch-search-result';
import { getSkipperErrorHeader } from '../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class SecondQualityDispatchProxyService {
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/second-quality-dispatch';

  constructor(private http: HttpClient) {}

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<SecondQualityDispatch>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.http.get<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}`, { headers });
  }

  public search(command: SecondQualityDispatchSearchCommand): Observable<ApiResponse<SecondQualityDispatch[]>> {
    return this.http.post<ApiResponse<SecondQualityDispatch[]>>(`${this.baseUrl}/search`, command);
  }

  public searchPaginated(
    command: SecondQualityDispatchSearchCommand,
    pageNumber = 1,
    pageSize = 25,
  ): Observable<ApiResponse<SecondQualityDispatchSearchResult>> {
    return this.http.post<ApiResponse<SecondQualityDispatchSearchResult>>(
      `${this.baseUrl}/search-paginated?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      command,
    );
  }

  public create(command: SecondQualityDispatchInsertCommand): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.post<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}`, command);
  }

  public updateStatus(id: string, newStatus: SecondQualityDispatchStatus): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.post<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/status`, newStatus);
  }

  public scanRoll(id: string, rollNumber: string): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.post<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/scan-roll`, JSON.stringify(rollNumber), {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  public removeRoll(id: string, rollNumber: string): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.delete<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/roll/${encodeURIComponent(rollNumber)}`);
  }

  public updateItemLines(id: string, command: SecondQualityDispatchItemLinesUpdateCommand): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.patch<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/item-lines`, command);
  }

  public updateRollPrice(id: string, command: SecondQualityDispatchRollPriceUpdateCommand): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.patch<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/roll-price`, command);
  }

  public scanLotItem(id: string, lotItemNumber: string): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.post<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/scan-lot-item`, JSON.stringify(lotItemNumber), {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  public removeLotItem(id: string, lotItemNumber: string): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.delete<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/lot-item/${encodeURIComponent(lotItemNumber)}`);
  }

  public updateLotItemPrice(
    id: string,
    command: SecondQualityDispatchLotItemPriceUpdateCommand,
  ): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.patch<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/lot-item-price`, command);
  }

  public updateShippingInfo(
    id: string,
    command: SecondQualityDispatchShippingUpdateCommand,
  ): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.patch<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/shipping-info`, command);
  }

  public updateBillingInfo(id: string, command: SecondQualityDispatchBillingUpdateCommand): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.patch<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/billing-info`, command);
  }

  public ensureInvoiceNumber(id: string): Observable<ApiResponse<SecondQualityDispatch>> {
    return this.http.post<ApiResponse<SecondQualityDispatch>>(`${this.baseUrl}/${id}/ensure-invoice-number`, {});
  }

  public downloadCommercialInvoice(id: string, isPreInvoice = false): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/report/commercial-invoice?isPreInvoice=${isPreInvoice}`, {
      responseType: 'blob',
    });
  }
}
